import React from "react";
import { Toggle } from "@nuorder/components";
import { formatMessage } from "lib/globalize-util";
import { TextField, makeStyles } from "@material-ui/core";
import theme from "styles/theme";
import { List, Map } from "immutable";
import { Metric } from "helpers/metrics/types";
import { ISchemaKey } from "helpers/product-collection";
import { useTargetWizardContext } from "../context/target-wizard-context";
import "./wizard-step-one.css";
import { GridApi } from "ag-grid-community";
import { ControlledSelect } from "./controlled-select";
import { SelectValueProvider } from "./select-value-context";
import { useTargetForm, ASSORTMENT_TARGET_TYPE, FormFieldName } from "../hooks/useTargetForm/useTargetForm";
import { Kinds } from "constants/product-collection";
import { Controller } from "react-hook-form";
import { TargetGroupWithTargets } from "../../types";
import { IAllocationDimension } from "helpers/allocation-dimension";
import { validateTargetName } from "./targets-form-validation";
import { capitalize } from "lodash";

const useStyles = makeStyles(() => ({
    helperText: {
        "&.MuiFormHelperText-root": {
            marginRight: 0,
            textAlign: "end",
        },
    },
    select: {
        padding: "8px 12px",
    },
    textFieldRoot: {
        "& fieldset": {
            borderColor: "#BDBDBD",
        },
        "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#00A9A4",
        },
    },
}));

interface OwnProps {
    merchantId: string;
    schema: List<Map<string, any>>;
    metrics: Metric[];
    gridApi: GridApi;
    genericAllocationDimensions: IAllocationDimension[];
    targetGroups: List<Map<string, any>>;
}
interface HandleMethods {
    updateFormToSnapshot: () => TargetGroupWithTargets;
}

const NAME_IS_VALID = "nameIsValid";

export const WizardStepOne = React.forwardRef<HandleMethods, OwnProps>(
    ({ merchantId, schema, metrics, gridApi, genericAllocationDimensions, targetGroups }, ref) => {
        const classes = useStyles();
        const {
            targetGroupWithTargetsSnapshot,
            targetGroupFormIsValid: [, setTargetGroupFormIsValid],
            renderedComponent: [renderedComponent],
        } = useTargetWizardContext();

        const [dependencyFields, setDependencyFields] = React.useState<ISchemaKey[]>([]);
        const filteredMetrics = React.useMemo(() => metrics.filter((metric) => metric.isTargetMetric), [metrics]);
        const metricsOptions = React.useMemo(
            () => filteredMetrics.map(({ displayName, kind }) => ({ label: displayName, value: kind })),
            [filteredMetrics]
        );

        const [stepOneForm, updateFormToSnapshot] = useTargetForm(
            targetGroupWithTargetsSnapshot[0],
            genericAllocationDimensions,
            dependencyFields,
            metricsOptions
        );
        const { control, formState, getValues, resetField, watch, unregister, trigger } = stepOneForm;
        const { errors } = formState;

        const [assortmentsValuesOnlyToggleValue, setAssortmentsValuesOnlyToggleValue] = React.useState(false);

        const schemaJs: ISchemaKey[] = React.useMemo(() => schema.toJS(), [schema]);

        const fieldsWatcher = watch();
        const targetType = fieldsWatcher[FormFieldName.Type];
        const shouldShowSecondSection = React.useMemo(() => {
            // if metric type is assortment we don't show the next section, user can go to next step
            if (targetType === ASSORTMENT_TARGET_TYPE) {
                return false;
            }
            return Object.keys(fieldsWatcher).every((key) => {
                const value = fieldsWatcher[key];

                // When this is schema fields, check the inner fields instead
                if (key === FormFieldName.SchemaFields) {
                    return dependencyFields.every((dependencyField) => !!value[dependencyField.field_name]);
                }

                return !!value;
            });
        }, [fieldsWatcher, targetType, dependencyFields]);

        const targetTypeSchemaKey = React.useMemo(() => {
            const fieldFromSchema = schemaJs.find((schemaField) => {
                return schemaField.field_name === targetType;
            });

            return fieldFromSchema;
        }, [targetType, schemaJs]);

        const [targetFieldOptions, lookupFieldsMap]: [
            { label: string; value: string }[],
            { [key: string]: ISchemaKey[] },
        ] = React.useMemo(() => {
            const { schemaFieldOptions, lookupFieldsMap } = schemaJs.reduce(
                (acc, schemaField) => {
                    const isTargetEnabled = schemaField.target_view?.is_target_type;
                    if (isTargetEnabled) {
                        acc.schemaFieldOptions.push({
                            label: schemaField.display_name_override || schemaField.display_name,
                            value: schemaField.field_name,
                        });
                    }

                    // Mapping the lookups for each field so we don't have to loop again
                    const lookupPlan = schemaField.lookup_plan;

                    if (lookupPlan) {
                        lookupPlan.where.fields.forEach(({ column }) => {
                            const lookupField = acc.lookupFieldsMap[schemaField.field_name];
                            const dependecySchemaField = schemaJs.find(
                                (field) => field.field_name === column.schema_column
                            );
                            if (lookupField) {
                                acc.lookupFieldsMap[schemaField.field_name] = [...lookupField, dependecySchemaField];
                            } else {
                                acc.lookupFieldsMap[schemaField.field_name] = [dependecySchemaField];
                            }
                        });
                    }

                    return acc;
                },
                { schemaFieldOptions: [], lookupFieldsMap: {} }
            );

            const assortmentOption = {
                label: capitalize(ASSORTMENT_TARGET_TYPE),
                value: ASSORTMENT_TARGET_TYPE,
            };

            const optionsWithAssortmentOption = [assortmentOption, ...schemaFieldOptions];

            return [optionsWithAssortmentOption, lookupFieldsMap];
        }, [schemaJs]);

        // triggers validation on mount
        React.useEffect(() => {
            trigger();
        }, [trigger]);

        React.useEffect(() => {
            setTargetGroupFormIsValid(formState.isValid);
        }, [setTargetGroupFormIsValid, fieldsWatcher, formState.isValid]);

        const handleTargetTypeFieldChange = () => {
            // IMPORTANT: `dependencyFields` here is not the latest. Which is good, we want to unregister old fields
            dependencyFields.forEach((dep) => {
                unregister(`${FormFieldName.SchemaFields}.${dep.field_name}`);
            });
            resetField(FormFieldName.Values, { defaultValue: [] });
        };

        const handleSchemaFieldsChange = () => {
            resetField(FormFieldName.Values, { defaultValue: [] });
        };

        const handleAssortmentValuesOnlyToggleChange = (_: React.MouseEvent, checked: boolean) => {
            setAssortmentsValuesOnlyToggleValue(checked);
        };

        const buildDependencyFields = React.useCallback(
            (lookupFieldKey: string) => {
                const dependeciesFields = lookupFieldsMap[lookupFieldKey];
                if (dependeciesFields) {
                    const sortedDependencies = recursiveMapLookupByPriority(dependeciesFields, lookupFieldsMap);
                    setDependencyFields(sortedDependencies);
                } else {
                    setDependencyFields([]);
                }
            },
            [lookupFieldsMap]
        );

        React.useEffect(() => {
            if (targetTypeSchemaKey?.kind === Kinds.DEPARTMENT_LOOKUP) {
                setAssortmentsValuesOnlyToggleValue(true);
            } else {
                setAssortmentsValuesOnlyToggleValue(false);
            }
            targetType && buildDependencyFields(targetType);
        }, [targetType, targetTypeSchemaKey, buildDependencyFields]);

        React.useImperativeHandle(ref, () => {
            return {
                updateFormToSnapshot: () => {
                    const snapshot = updateFormToSnapshot();
                    return snapshot;
                },
            };
        }, [updateFormToSnapshot]);

        const duplicateTargetMessage =
            errors && errors[FormFieldName.Name] && errors[FormFieldName.Name].type === NAME_IS_VALID
                ? errors[FormFieldName.Name].message
                : undefined;

        const currentSchemaLookUpOptions = buildLookupOptions(merchantId, targetTypeSchemaKey, dependencyFields, watch);

        return (
            <>
                <form className="financial-target-first-form-container">
                    <div className="financial-target-first-form-dialog-first-section">
                        <div className="financial-target-first-form-input-container">
                            <label
                                className="financial-target-first-form-form-label"
                                style={{ color: duplicateTargetMessage ? theme.palette.errorColor : "" }}
                                htmlFor={FormFieldName.Name}
                            >
                                {formatMessage("Target name")}
                            </label>
                            <Controller
                                render={({ field }) => {
                                    return (
                                        <TextField
                                            variant="outlined"
                                            id={FormFieldName.Name}
                                            inputProps={{ maxLength: 36 }}
                                            autoFocus
                                            helperText={`${fieldsWatcher?.name?.length || 0}/${36}`}
                                            fullWidth
                                            InputProps={{
                                                classes: {
                                                    root: classes.textFieldRoot,
                                                },
                                            }}
                                            FormHelperTextProps={{
                                                classes: {
                                                    root: classes.helperText,
                                                },
                                            }}
                                            {...field}
                                        />
                                    );
                                }}
                                name={FormFieldName.Name}
                                control={control}
                                rules={
                                    {
                                        required: true,
                                        validate: {
                                            [NAME_IS_VALID]: (v: string) =>
                                                validateTargetName(v, targetGroups, targetGroupWithTargetsSnapshot[0]),
                                        },
                                    } as any
                                }
                            />
                            {duplicateTargetMessage && (
                                <p style={{ color: theme.palette.errorColor }}>{duplicateTargetMessage}</p>
                            )}
                        </div>
                        <div className="financial-target-first-form-input-container">
                            <label className="financial-target-first-form-form-label" htmlFor={FormFieldName.Metric}>
                                {formatMessage("Target metric")}
                            </label>
                            <SelectValueProvider
                                gridApi={gridApi}
                                lookupOptions={{
                                    merchantId,
                                    dependenciesValues: {},
                                }}
                                options={metricsOptions}
                            >
                                <ControlledSelect
                                    name={FormFieldName.Metric}
                                    control={control}
                                    id={FormFieldName.Metric}
                                    testId={"target-metric-select"}
                                    required={true}
                                    isSearchable
                                    placeholder={null}
                                />
                            </SelectValueProvider>
                        </div>
                        <div className="financial-target-first-form-input-container">
                            <label className="financial-target-first-form-form-label" htmlFor={FormFieldName.Type}>
                                {formatMessage("Target type")}
                            </label>
                            <SelectValueProvider
                                gridApi={gridApi}
                                lookupOptions={{
                                    merchantId,
                                    dependenciesValues: {},
                                }}
                                options={targetFieldOptions}
                            >
                                <ControlledSelect
                                    control={control}
                                    name={FormFieldName.Type}
                                    id={FormFieldName.Type}
                                    required
                                    onChange={handleTargetTypeFieldChange}
                                    isSearchable
                                    placeholder={null}
                                />
                            </SelectValueProvider>
                        </div>
                        {dependencyFields.map((field, index) => {
                            const previousField = index !== 0 && dependencyFields[index - 1];
                            const previousFieldValue = getValues(
                                `${FormFieldName.SchemaFields}.${previousField.field_name}`
                            );

                            if (index !== 0 && !previousFieldValue) {
                                return null;
                            }

                            return (
                                <div
                                    key={`${FormFieldName.SchemaFields}.${field.field_name}`}
                                    className="financial-target-first-form-input-container"
                                >
                                    <label
                                        className="financial-target-first-form-form-label"
                                        htmlFor={field.field_name}
                                    >
                                        {field.display_name}
                                    </label>
                                    <SelectValueProvider
                                        gridApi={gridApi}
                                        lookupOptions={{
                                            merchantId,
                                            schema: field,
                                            dependenciesValues: getDependenciesForDependecyField(index, {
                                                schema: previousField,
                                                value: previousFieldValue,
                                            }),
                                        }}
                                    >
                                        <ControlledSelect
                                            control={control}
                                            name={`${FormFieldName.SchemaFields}.${field.field_name}`}
                                            id={`${FormFieldName.SchemaFields}.${field.field_name}`}
                                            testId={`${FormFieldName.SchemaFields}.${field.field_name}`}
                                            required
                                            onChange={handleSchemaFieldsChange}
                                            isSearchable
                                            placeholder={null}
                                        />
                                    </SelectValueProvider>
                                </div>
                            );
                        })}
                    </div>
                    {/* Second section, conditionally renderered */}
                    {shouldShowSecondSection && (
                        <>
                            <div className="financial-target-first-form-separator" />
                            <div className="financial-target-first-form-dialog-second-section">
                                {currentSchemaLookUpOptions.schema?.lookup_plan && (
                                    <div
                                        data-test="in-assortment-toggle"
                                        className="financial-target-first-form-toggle-container"
                                    >
                                        <div>
                                            <Toggle
                                                className="wizard-toggle"
                                                checked={assortmentsValuesOnlyToggleValue}
                                                onChange={handleAssortmentValuesOnlyToggleChange}
                                            />
                                        </div>
                                        <div>{formatMessage("Show in-assortment values only.")}</div>
                                    </div>
                                )}
                                <div>
                                    <label
                                        htmlFor={FormFieldName.Values}
                                        className="financial-target-first-form-form-label"
                                    >
                                        {formatMessage("Select one or multiple")}
                                    </label>
                                    <SelectValueProvider
                                        gridApi={gridApi}
                                        useAssortmentValues={assortmentsValuesOnlyToggleValue}
                                        lookupOptions={currentSchemaLookUpOptions}
                                    >
                                        <ControlledSelect
                                            control={control}
                                            required
                                            id={FormFieldName.Values}
                                            name={FormFieldName.Values}
                                            isMulti
                                            isSearchable
                                            removeUnavailableOptions={assortmentsValuesOnlyToggleValue}
                                            placeholder={null}
                                        />
                                    </SelectValueProvider>
                                </div>
                            </div>
                        </>
                    )}
                </form>
            </>
        );
    }
);

const recursiveMapLookupByPriority = (dependeciesFields: ISchemaKey[], maps: any, accumulator: any[] = []) => {
    const accCopy = [...accumulator];
    return dependeciesFields.reduce((acc, field) => {
        const parentDependecy = maps[field.field_name];
        const fieldAlreadyInAccIndex = acc.findIndex((currentField) => currentField.field_name === field.field_name);
        if (fieldAlreadyInAccIndex > -1) {
            acc.splice(fieldAlreadyInAccIndex, 1);
        }
        if (parentDependecy) {
            acc.unshift(field);
            const parentLookups = recursiveMapLookupByPriority(parentDependecy, maps, acc);
            acc = parentLookups;
        } else {
            acc.unshift(field);
        }
        return acc;
    }, accCopy);
};

const buildLookupOptions = (merchantId, schemaKey, dependeciesFields, watch) => {
    return {
        merchantId,
        schema: schemaKey,
        dependenciesValues: dependeciesFields.reduce((acc, dep) => {
            acc[dep.field_name] = {
                value: watch(`${FormFieldName.SchemaFields}.${dep.field_name}`),
                type: dep.value_type,
            };

            return acc;
        }, {}),
    };
};

const getDependenciesForDependecyField = (index: number, previousField: { schema: ISchemaKey; value: string }) => {
    const { schema, value } = previousField || {};

    // means there is no previous dependency field for the first item
    if (index === 0) {
        return {};
    }

    return {
        [schema.field_name]: {
            type: schema.value_type,
            value: value,
        },
    };
};
